import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Language } from '@lx/language';
import { provideComponentStore } from '@ngrx/component-store';
import { delay, filter, map, Observable, switchMap, take } from 'rxjs';
import { OnlineSchoolService } from '../../services';
import { CourseState, CourseStore, SharedDataStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CoursePayload } from '../../types';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { Timezone } from '@lx/quote-tool/shared/types';
import { ClassTime } from '@lx/quote-tool/lite/online/types';
import { ClassTimeSelectorFieldComponent, OnlineCoursePredefinedSelection } from '@lx/quote-tool/lite/online/ui';
import { DurationSelectorFieldComponent } from '../duration-selector-field/duration-selector-field.component';
import { FlexibleStartDateSelectorFieldComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { OnlineCourseSelectorFieldComponent } from '../online-course-selector-field/online-course-selector-field.component';
import { TimezoneSelectorFieldComponent } from '@lx/quote-tool/lite/online/ui';
import { TimezoneService, TripDatesService } from '@lx/quote-tool/shared/services';
import { CalendarConfig } from '@lx/shared/ui/calendar-field';

@Component({
  standalone: true,
  selector: 'lx-online-courses',
  templateUrl: './online-courses.component.html',
  styleUrls: ['./online-courses.component.scss'],
  imports: [
    OnlineCourseSelectorFieldComponent,
    DurationSelectorFieldComponent,
    ClassTimeSelectorFieldComponent,
    TimezoneSelectorFieldComponent,
    CommonModule,
    FormsModule,
    FlexibleStartDateSelectorFieldComponent,
  ],
  providers: [provideComponentStore(CourseStore)],
})
export class OnlineCoursesComponent implements OnInit {
  @Output() coursePayload = new EventEmitter<CoursePayload>();
  @Output() validCoursePayload = new EventEmitter<boolean>();
  courseDetails$: Observable<Partial<CourseState>> | undefined;
  selectedSchoolLocationAndCourseAndTimezone$:
    | Observable<{ selectedSchoolLocation?: SchoolLocation; selectedCourse?: CourseDetails; selectedTimezone?: Timezone }>
    | undefined;
  languages$: Observable<Language[]> | undefined;
  calendarConfig: CalendarConfig | undefined;

  private readonly courseStore = inject(CourseStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onlineSchoolService = inject(OnlineSchoolService);
  private readonly tripDatesService = inject(TripDatesService);
  private readonly timezoneService = inject(TimezoneService);

  constructor() {
    this.languages$ = this.sharedDataStore.languages$;
    this.courseDetails$ = this.courseStore.courseDetails$;
    this.selectedSchoolLocationAndCourseAndTimezone$ = this.courseStore.selectedSchoolLocationAndCourseAndTimezone$;
  }

  ngOnInit() {
    this.courseStore.isOnlineValid$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valid) => this.validCoursePayload.emit(valid));
    this.courseDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((courseDetails) => this.coursePayload.emit(courseDetails));
    this.setOnlineSchoolLocation();

    // Fetch calendar config based on selected school location
    this.courseStore.selectedSchoolLocation$
      .pipe(
        switchMap((selectedSchoolLocation) => this.tripDatesService.getCalendarConfig(selectedSchoolLocation?.schoolCode)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((calendarConfig) => {
        this.calendarConfig = calendarConfig;
      });

    // Initialize the selected timezone if not already set
    this.courseStore.selectedTimezone$
      .pipe(
        filter((selectedTimezone) => !selectedTimezone),
        switchMap(() => this.timezoneService.timezones$),
        map((timezones) => this.timezoneService.buildTimezoneFromBrowser(timezones)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((defaultTimezone: Timezone) => {
        this.updateSelectedTimezone(defaultTimezone);
      });
  }

  updateSelectedCourse(selectedCourse: CourseDetails) {
    this.updateValueInStore(selectedCourse, 'selectedCourse');
  }

  handlePredefinedSelections(predefinedSelections: OnlineCoursePredefinedSelection): void {
    if (predefinedSelections.duration !== undefined) {
      this.updateSelectedDuration(predefinedSelections.duration);
    }
    if (predefinedSelections.startDate !== undefined) {
      this.updateSelectedStartDate(predefinedSelections.startDate);
    }
  }

  updateSelectedSchoolLocation(schoolLocation: SchoolLocation) {
    this.updateValueInStore(schoolLocation, 'selectedSchoolLocation');
  }

  updateSelectedDuration(selectedDuration: number) {
    this.updateValueInStore(selectedDuration, 'selectedDuration');
  }

  updateSelectedStartDate(selectedStartDate: string) {
    this.updateValueInStore(selectedStartDate, 'selectedStartDate');
  }

  updateSelectedClassTime(selectedClassTime: ClassTime) {
    this.updateValueInStore(selectedClassTime, 'selectedClassTime');
  }

  updateSelectedTimezone(selectedTimezone: Timezone) {
    this.updateValueInStore(selectedTimezone, 'selectedTimezone');
  }

  private updateValueInStore(value: string | CourseDetails | Timezone | SchoolLocation | ClassTime | number | undefined, key: keyof CoursePayload) {
    this.courseStore.updateState({
      [key]: value,
    });
  }

  private setOnlineSchoolLocation(): void {
    this.courseStore.selectedSchoolLocationAndLanguage$
      .pipe(
        // Only proceed if selectedSchoolLocation is not set
        filter(({ selectedSchoolLocation }) => !selectedSchoolLocation),
        // Fetch online school locations for the selected language
        switchMap(({ selectedLanguage }) =>
          this.onlineSchoolService.getOnlineGroupedSchoolLocations(selectedLanguage).pipe(
            take(1),
            filter((onlineSchool) => !!onlineSchool[0]),
            delay(500),
            takeUntilDestroyed(this.destroyRef),
          ),
        ),
      )
      .subscribe((onlineSchool) => {
        const onlineSchoolLocation: SchoolLocation = {
          countryCode: onlineSchool[0]?.value,
          countryName: onlineSchool[0]?.label,
          schoolCode: onlineSchool[0]?.items[0]?.value,
          schoolName: onlineSchool[0]?.items[0]?.label,
        };

        this.updateSelectedSchoolLocation(onlineSchoolLocation);
      });
  }
}
